/**
 * Shopify Form Handler
 * 
 * This script manages Shopify embedded forms by:
 * 1. Hiding the default teaser buttons to prevent direct user interaction
 * 2. Allowing custom triggers (links/buttons with href="#form-*") to open forms
 * 3. Continuously monitoring DOM changes to re-hide buttons when forms close
 * 4. Clears form dismissal localStorage on page reload
 * 
 * Usage: Add href="#form-{formId}" to any element to trigger the form
 * Example: <a href="#form-493827">Open Form</a>
 */

(function() {
  'use strict';

  let isObserverSetup = false;
  /** @type {number | null} */
  let checkInterval = null;

  /**
   * Clears localStorage items matching pattern: {formId}-dismissed-after-submit
   */
  function clearFormDismissalStorage() {
    try {
      Object.keys(localStorage).forEach(key => {
        if (/^\d+-dismissed-after-submit$/.test(key)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      // Silently handle localStorage access errors
    }
  }

  /**
   * Finds the teaser button in shadow DOM
   * @param {HTMLElement | null} embed - The form embed element
   * @returns {HTMLElement|null} - The button element or null
   */
  function findTeaserButton(embed) {
    if (!embed || !embed.shadowRoot) return null;

    const isShopifyFormsEmbed = embed.tagName.toLowerCase() === 'shopify-forms-embed';
    const selectors = isShopifyFormsEmbed 
      ? ['[class*="teaserContainer"]', 'button[aria-haspopup="dialog"]']
      : ['button[data-testid="teaser-container"]', 'button[class*="teaser"]'];

    for (const selector of selectors) {
      const button = embed.shadowRoot.querySelector(selector);
      if (button instanceof HTMLElement) return button;
    }
    return null;
  }

  /**
   * Finds and manages the teaser button
   * @param {boolean} click - Whether to click the button
   * @param {boolean} show - Whether to show or hide the button
   * @returns {boolean} - Success status
   */
  function manageTeaserButton(click = false, show = false) {
    try {
      const embed = document.querySelector('shopify-forms-embed') || document.querySelector('form-embed');
      const button = findTeaserButton(embed instanceof HTMLElement ? embed : null);
      
      if (!button) return false;

      button.style.display = show ? '' : 'none';

      if (click && show) {
        button.click();
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Hides any visible teaser buttons
   */
  function hideVisibleButtons() {
    try {
      const embed = document.querySelector('shopify-forms-embed') || document.querySelector('form-embed');
      const button = findTeaserButton(embed instanceof HTMLElement ? embed : null);
      
      if (button && button.style.display !== 'none') {
        button.style.display = 'none';
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  /**
   * Sets up MutationObserver to monitor DOM changes
   */
  function setupObserver() {
    if (isObserverSetup) return;

    try {
      const observer = new MutationObserver(() => {
        hideVisibleButtons();
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Observe shadow DOM changes for all form embeds
      document.querySelectorAll('shopify-forms-embed, form-embed').forEach(embed => {
        if (embed.shadowRoot) {
          const shadowObserver = new MutationObserver(hideVisibleButtons);
          shadowObserver.observe(embed.shadowRoot, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style']
          });
        }
      });

      isObserverSetup = true;
    } catch (error) {
      // Fallback to interval checking if MutationObserver fails
      if (checkInterval === null) {
        checkInterval = window.setInterval(hideVisibleButtons, 2000);
      }
    }
  }

  /**
   * Initialize form management
   */
  function initialize() {
    clearFormDismissalStorage();
    manageTeaserButton(false, false);
    setupObserver();
    
    if (checkInterval !== null) {
      clearInterval(checkInterval);
      checkInterval = null;
    }
  }

  /**
   * Opens a form by ID with retry logic
   * @param {string} formId - The form ID (numeric part, e.g., "520665")
   * @param {string} hash - The full hash (e.g., "#form-520665")
   */
  function openFormWithRetry(formId, hash) {
    window.location.hash = hash;
    
    let attempts = 0;
    const maxAttempts = 30;
    
    const tryOpen = () => {
      attempts++;
      
      // Try to find form embed
      let embed = document.querySelector('shopify-forms-embed') || document.querySelector('form-embed');
      
      if (!embed || !embed.shadowRoot) {
        if (attempts < maxAttempts) {
          setTimeout(tryOpen, 300);
        }
        return;
      }
      
      // Use manageTeaserButton to show and click
      if (manageTeaserButton(true, true)) {
        return; // Success
      }
      
      // Retry if button not found yet
      if (attempts < maxAttempts) {
        setTimeout(tryOpen, 300);
      }
    };
    
    tryOpen();
  }

  /**
   * Handle custom trigger clicks
   * @param {Event} event - Click event
   */
  function handleTriggerClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = target.closest('a[href^="#form-"], button[href^="#form-"]') || 
                    ((target.tagName === 'A' || target.tagName === 'BUTTON') && target.getAttribute('href')?.startsWith('#form-') ? target : null);
    
    if (!trigger) return;

    const href = trigger.getAttribute('href');
    if (!href) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const formIdMatch = href.match(/^#form-(\d+)/);
    if (formIdMatch?.[1]) {
      openFormWithRetry(formIdMatch[1], href);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Set up periodic check for late-loading forms
  checkInterval = window.setInterval(() => {
    if (manageTeaserButton(false, false)) {
      setupObserver();
      if (checkInterval !== null) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    }
  }, 1000);

  // Handle custom trigger clicks
  document.addEventListener('click', handleTriggerClick);

})();


